/**
 * PaymentPricesList
 * Bloque pequeño que muestra el mismo producto a precios calculados
 * según los métodos de pago configurados en /admin/store (paymentMethods
 * con adjustPercent != 0). Sirve para que el cliente vea "Efectivo -2% =
 * $13.720" sin entrar al carrito.
 *
 * Props:
 *  - basePrice (number)  → precio base sobre el cual aplicar el ajuste.
 *  - paymentMethods (array) → storeSettings.paymentMethods
 *  - variant: "compact" | "full"
 *  - allowedRestricted (array) → keys de métodos restringidos que este
 *    usuario tiene habilitados (de user.allowedPaymentMethods).
 */
import React from 'react';
import { Wallet } from 'lucide-react';

const fmt = (n) => `$${Math.ceil(n || 0).toLocaleString('es-AR')}`;

const PaymentPricesList = ({ basePrice = 0, paymentMethods = [], variant = 'compact', allowedRestricted = [] }) => {
  const methods = (paymentMethods || [])
    .filter(m => m && m.enabled !== false && Number(m.adjustPercent || 0) !== 0)
    .filter(m => !m.restricted || (allowedRestricted || []).includes(m.key));

  if (!basePrice || methods.length === 0) return null;

  // Helper: label abreviado a 3-4 letras para variantes compactas
  const shortLabel = (label) => {
    const s = (label || '').trim();
    if (s.length <= 6) return s;
    // "Retirando por depósito" → "Retiro"
    // "Transferencia"           → "Transf"
    // "Efectivo"                → "Efectivo" (entra)
    const lower = s.toLowerCase();
    if (lower.startsWith('retir')) return 'Retiro';
    if (lower.startsWith('efect')) return 'Efvo';
    if (lower.startsWith('trans')) return 'Transf';
    if (lower.startsWith('tarj'))  return 'Tarjeta';
    if (lower.startsWith('cuent')) return 'Cta Cte';
    if (lower.startsWith('mp') || lower.startsWith('mercad')) return 'MP';
    return s.split(' ')[0].slice(0, 6);
  };

  // 🔸 Variante TINY: chips inline ultra-compactos para carruseles.
  if (variant === 'tiny') {
    return (
      <div className="flex flex-wrap gap-1 mt-1" data-testid="payment-prices-tiny">
        {methods.slice(0, 3).map(m => {
          const adj = Number(m.adjustPercent || 0);
          const adjusted = Math.ceil(basePrice * (1 + adj / 100));
          const isDiscount = adj < 0;
          return (
            <span key={m.key}
              data-testid={`payment-tiny-${m.key}`}
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                isDiscount ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
              } tabular-nums`}
              title={`${m.label} (${adj > 0 ? '+' : ''}${adj}%)`}
            >
              {shortLabel(m.label)} {adj > 0 ? '+' : ''}{adj}% · ${adjusted.toLocaleString('es-AR')}
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`rounded-lg border border-emerald-100 bg-emerald-50/40 ${variant === 'full' ? 'p-3' : 'p-2'} mt-2`}
      data-testid="payment-prices-list">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Wallet size={variant === 'full' ? 14 : 12} className="text-emerald-700" />
        <span className={`font-black uppercase tracking-wider text-emerald-800 ${variant === 'full' ? 'text-[11px]' : 'text-[10px]'}`}>
          Otros precios por forma de pago
        </span>
      </div>
      <ul className="space-y-0.5">
        {methods.map(m => {
          const adj = Number(m.adjustPercent || 0);
          const adjusted = basePrice * (1 + adj / 100);
          const isDiscount = adj < 0;
          return (
            <li key={m.key}
              data-testid={`payment-price-${m.key}`}
              className={`flex items-center justify-between ${variant === 'full' ? 'text-sm' : 'text-xs'}`}>
              <span className="text-zinc-700 font-medium truncate pr-2">
                {m.label}
                <span className={`ml-1.5 font-bold ${isDiscount ? 'text-emerald-700' : 'text-rose-600'}`}>
                  ({adj > 0 ? '+' : ''}{adj}%)
                </span>
              </span>
              <span className={`font-black tabular-nums ${isDiscount ? 'text-emerald-800' : 'text-rose-700'}`}>
                {fmt(adjusted)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PaymentPricesList;
